// Inline email composer (kula_email_compose). Its own envelope, detected
// before the table path.

import type { BlockOutput, EmailComposeBlockData } from '../../types';
import { arr, str, strArr, toId } from './guards';
import type { StructuredEmailCompose } from './types';

const DRAFT_TOOL_CALL_DELIMITER = '::draft::';

const nonNegativeInt = (value: unknown): number | undefined =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  Number.isFinite(value) &&
  value >= 0
    ? value
    : undefined;

const composeData = (
  value: Record<string, unknown>,
  toolCallId?: string,
  draftId?: string,
  sent = false
): EmailComposeBlockData | null => {
  const entity = value.entity === 'application' ? 'application' : 'contact';
  const recipients = arr(value.recipients)
    .map((r) => {
      if (typeof r !== 'object' || r === null) return null;
      const obj = r as { id?: unknown; name?: unknown; email?: unknown };
      const id = toId(obj.id) ?? '';
      if (!id) return null;
      return { id, name: str(obj.name), email: str(obj.email) };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
  if (recipients.length === 0) return null;
  return {
    entity,
    recipients,
    subject: str(value.subject),
    body: str(value.body),
    jobId: toId(value.job_id),
    draftId,
    toolCallId,
    sent: sent || value.sent === true,
  };
};

export const buildEmailComposeBlock = (
  t: StructuredEmailCompose,
  toolCallId?: string
): BlockOutput | null => {
  const rawDrafts = arr(t.drafts);
  if (rawDrafts.length > 0) {
    const sentDraftKeys = new Set(strArr(t.sent_draft_keys));
    const seenDraftIds = new Set<string>();
    const drafts = rawDrafts.flatMap((rawDraft) => {
      if (typeof rawDraft !== 'object' || rawDraft === null) return [];
      const draft = rawDraft as Record<string, unknown>;
      if (draft.type !== 'email_compose') return [];
      const draftId = str(draft.draft_id)?.trim();
      if (!draftId || seenDraftIds.has(draftId)) return [];
      seenDraftIds.add(draftId);
      const childToolCallId = toolCallId
        ? `${toolCallId}${DRAFT_TOOL_CALL_DELIMITER}${draftId}`
        : undefined;
      const data = composeData(
        draft,
        childToolCallId,
        draftId,
        sentDraftKeys.has(draftId)
      );
      return data ? [data] : [];
    });
    if (drafts.length > 0) {
      // During a backend-first rollout, an older frontend can render and send
      // the batch envelope's top-level first-draft fallback. That legacy path
      // marks `sent: true` on the parent payload. Preserve exactly that first
      // child's terminal state; the remaining children stay independently
      // controlled by sent_draft_keys.
      if (t.sent === true && !drafts[0].sent) {
        drafts[0] = { ...drafts[0], sent: true };
      }
      const failedCount = nonNegativeInt(t.failed_count) ?? 0;
      const successfulCount =
        nonNegativeInt(t.successful_count) ?? drafts.length;
      const requestedCount =
        nonNegativeInt(t.requested_count) ??
        Math.max(successfulCount, drafts.length) + failedCount;
      return {
        type: 'emailComposeGroup',
        data: {
          drafts,
          requestedCount: Math.max(requestedCount, drafts.length),
          successfulCount: Math.max(successfulCount, drafts.length),
          failedCount,
        },
      };
    }
  }

  const data = composeData(t as unknown as Record<string, unknown>, toolCallId);
  if (!data) return null;
  return {
    type: 'emailCompose',
    data,
  };
};
